import { useState, useRef, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';

import BeatLoader from "react-spinners/BeatLoader";

import { BsTrash } from 'react-icons/bs';
import { FiMinus, FiPlus } from 'react-icons/fi';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { MdDeliveryDining } from 'react-icons/md';
import { TbShoppingCartOff } from "react-icons/tb";

import { Product } from '../../types/product';
import { OrderType, ProductCart } from '../../types/cart';

import { products } from '../../data/products';

import { useCart } from '../../hooks/useCart';

import { WhatsApp } from '../../services/whatsapp';
import { WhatsAppTemplate } from '../../services/whatsapp-template';

import { BaseLayout } from '../../layouts/BaseLayout';

const PHONE_NUMBER = '+5583981057031';
const DEFAULT_FINISHING_CART_LOADING_VALUE = 2000;

const ordersTypeMenu = {
  [OrderType.Retirada]: {
    text: 'Retirada',
    Icon: () => <FaMapMarkerAlt className="text-md" />,
  },
  [OrderType.Entrega]: {
    text: 'Entrega',
    Icon: () => <MdDeliveryDining className="text-2xl" />,
  }
};

const formatPrice = (price: number) => {
  const formatter = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

  return formatter.format(price);
};

const getOrderTypeMenuClass = (actived: boolean) => {
  return `px-4 py-2 flex gap-1 items-center justify-center text-sm rounded-md border border-solid ${actived ? 'bg-red-400 text-white border-transparent' : 'text-red-500 border-red-500'}`
};

const getCartCounterDescription = (count: number) => {
  const word = count === 1 ? 'item' : 'itens';

  return `(${count} ${word} no seu carrinho)`;
};

const mapProductsWithImage = (productsCart: ProductCart[]) => {
  return productsCart.map((productCart) => {
    const productItem = products.find((product) => product.name === productCart.name);

    return {
      ...productCart,
      image: productItem?.image,
    }
  });
};

export const Checkout = () => {
  const [finishingCart, setFinishingCart] = useState(false);
  const deliverFormRef = useRef<HTMLFormElement | null>(null);
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const addressInputRef = useRef<HTMLInputElement | null>(null);
  const addressNumberInputRef = useRef<HTMLInputElement | null>(null);
  const neighborhoodInputRef = useRef<HTMLInputElement | null>(null);
  const addressComplementInputRef = useRef<HTMLInputElement | null>(null);
  const observationInputRef = useRef<HTMLTextAreaElement | null>(null);
  const [orderType, setOrderType] = useState(OrderType.Retirada);
  const {
    products,
    count,
    getTotal,
    resetCart,
    decrementProduct,
    incrementProduct,
    removeProduct,
  } = useCart();

  const navigate = useNavigate();

  const productsCart = mapProductsWithImage(products);
  const hasProducts = Boolean(productsCart.length);

  const handleResetCart = (e: React.MouseEvent) => {
    e.preventDefault();

    resetCart();
  };

  const handleRedirectToHome = (e: React.MouseEvent) => {
    e.preventDefault();

    navigate('/');
  };

  const handleFinishCart = (e: React.MouseEvent) => {
    e.preventDefault();

    setFinishingCart(true);

    const formData = {
      products,
      orderType,
      total: getTotal(),
      name: nameInputRef.current?.value,
      address: addressInputRef.current?.value,
      addressNumber: addressNumberInputRef.current?.value,
      neighborhood: neighborhoodInputRef.current?.value,
      complement: addressComplementInputRef.current?.value,
      observation: observationInputRef.current?.value,
    };

    const template = WhatsAppTemplate().build(formData);
    const chatLink = WhatsApp().buildChatLink(PHONE_NUMBER, template);

    resetCart();

    window.location.assign(chatLink);
  };

  useEffect(() => {
    const locationOnPageload = window.location.href;

    const reloadPageAction = () => {
      if (window.location.href !== locationOnPageload) {
        window.location.reload();
      }
    };

    window.addEventListener('popstate', reloadPageAction);

    return () => {
      window.removeEventListener('popstate', reloadPageAction);
    }
  }, []);

  return (
    <BaseLayout>
      <section className="px-4 mt-4 mb-36">
        {finishingCart && (
          <div className="flex flex-col items-center justify-center gap-4 h-96">
            <BeatLoader
              size={24}
              color="tomato"
              loading
            />
            <h1 className="text-2xl text-gray-500">Estamos finalizando seu pedido</h1>
            <p className="text-sm text-center text-gray-400">Aguarde o redirecionamento para o link do WhatsApp.</p>
          </div>
        )}

        {!finishingCart && (
          <>
            {!hasProducts && (
              <div className="flex flex-col items-center justify-center gap-4 h-96">
                <TbShoppingCartOff className="text-4xl text-gray-600" />
                <h1 className="text-2xl text-gray-500">Seu carrinho está vazio</h1>
                <p className="text-sm text-center text-gray-400">Para visualizar os detalhes do perdido você precisa adicionar produtos ao carrinho.</p>
                <button
                  className="bg-red-600 min-w-36 px-2 py-4 text-white text-md rounded-xl"
                  onClick={handleRedirectToHome}
                >
                  Ver produtos
                </button>
              </div>
            )}

            {hasProducts && (
              <>
                <div className="flex gap-4">
                  {Object.entries(ordersTypeMenu).map(([type, { text, Icon }]) => (
                    <button
                      key={`order-type-menu-${type}`}
                      onClick={() => setOrderType(type as OrderType)}
                      className={getOrderTypeMenuClass(type === orderType)}
                    >
                      <Icon />
                      {text}
                    </button>
                  ))}
                </div>

                {orderType === OrderType.Retirada && (
                  <div className="flex flex-col gap-2 mt-5">
                    <h2 className="text-xl font-medium text-red-600">Retirada Estabelecimento</h2>
                    <p className="text-md font-bold text-gray-600">Horário Funcionamento: 07:00 às 19:00</p>
                    <p className="text-xs text-gray-500">Endereço: Avenida Prefeito Vitoriano Antunes N°000 - Centro - Cascavel-Ce</p>
                  </div>
                )}

                {orderType === OrderType.Entrega && (
                  <form ref={deliverFormRef} className="flex gap-2 flex-wrap mt-5">
                    <div className="flex flex-auto flex-col gap-1">
                      <label className="text-md text-gray-500">
                        Nome
                      </label>
                      <input
                        type="text"
                        ref={nameInputRef}
                        className="p-2 text-sm text-gray-500 outline-none border border-solid border-gray-400 rounded-md"
                      />
                    </div>
                    <div className="flex flex-auto flex-col gap-1">
                      <label className="text-md text-gray-500">
                        Endereço
                      </label>
                      <input
                        type="text"
                        ref={addressInputRef}
                        className="p-2 text-sm text-gray-500 outline-none border border-solid border-gray-400 rounded-md"
                      />
                    </div>
                    <div className="flex gap-4 w-full">
                      <div className="flex flex-col flex-auto gap-1 w-2/12">
                        <label className="text-md text-gray-500">
                          Número
                        </label>
                        <input
                          type="text"
                          ref={addressNumberInputRef}
                          className="p-2 text-sm text-gray-500 outline-none border border-solid border-gray-400 rounded-md"
                        />
                      </div>
                      <div className="flex flex-auto flex-col gap-1">
                        <label className="text-md text-gray-500">
                          Bairro
                        </label>
                        <input
                          type="text"
                          ref={neighborhoodInputRef}
                          className="p-2 text-sm text-gray-500 outline-none border border-solid border-gray-400 rounded-md"
                        />
                      </div>
                    </div>
                    <div className="flex flex-auto flex-col gap-1">
                      <label className="text-md text-gray-500">
                        Ponto de referência
                      </label>
                      <input
                        type="text"
                        ref={addressComplementInputRef}
                        className="p-2 text-sm text-gray-500 outline-none border border-solid border-gray-400 rounded-md"
                      />
                    </div>
                  </form>
                )}

                <div className="flex flex-col gap-2 mt-3 p-2 bg-white rounded-md">
                  <div className="flex justify-between pb-2 items-center ">
                    <p className="text-xl font-medium text-red-600">
                      Pedido
                    </p>
                    <p className="text-md font-medium text-gray-500">
                      {getCartCounterDescription(count)}
                    </p>
                  </div>

                  <div className="flex flex-col gap-1">
                    {productsCart.map((product, index) => (
                      <div
                        key={`product-cart-${index}`}
                        className="flex py-2 justify-between items-center border-b-2"
                      >
                        <img
                          className="size-14"
                          src={product.image}
                        />

                        <div className="flex flex-col flex-auto items-center gap-2">
                          <div className="text-xs text-gray-600">
                            {product.name}
                          </div>

                          <div className="flex gap-2 items-center justify-center">
                            <button
                              className="flex items-center justify-center w-6 h-6 p-1 text-2xl text-red-600 rounded-md border-gray-300 border border-solid"
                              onClick={() => decrementProduct(product)}
                            >
                              <FiMinus />
                            </button>

                            <div className="w-10 h-9 flex items-center justify-center rounded-md text-sm text-gray-600">
                              {product.quantity}
                            </div>

                            <button
                              className="flex items-center justify-center w-6 h-6 p-1 text-2xl text-red-600 rounded-md border-gray-300 border border-solid"
                              onClick={() => incrementProduct(product)}
                            >
                              <FiPlus />
                            </button>
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <p className="text-xs text-gray-600">
                            {formatPrice(Number(product.price * product.quantity))}
                          </p>
                          <BsTrash
                            className="text-lg text-red-600"
                            onClick={() => removeProduct(product as unknown as Product)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-between pb-2 items-center">
                    <p className="text-xl font-medium text-red-600">
                      Total
                    </p>
                    <p className="text-xl text-gray-600 font-bold">
                      {formatPrice(Number(getTotal()))}
                    </p>
                  </div>

                  <div className="flex flex-col my-2">
                    <p className="text-xl text-gray-600 font-medium">
                      Observações
                    </p>
                    <p className="text-xs text-gray-400">
                      Ex: Forma de pagamento, Fateado, cortado, ralado.
                    </p>
                    <textarea
                      rows={3}
                      ref={observationInputRef}
                      placeholder="Digite aqui"
                      className="p-2 mt-4 w-full rounded-md border border-solid border-gray-400 outline-none text-md text-gray-500"
                    />
                  </div>

                  <div className="w-full fixed bottom-0 left-0 bg-white border-t border-solid border-y-gray-200 px-4 py-6 flex items-center justify-center gap-4">
                    <button
                      className="flex flex-auto gap-2 items-center justify-center border border-solid border-red-600 px-2 py-4 text-red-600 text-md rounded-xl"
                      onClick={handleResetCart}
                    >
                      <TbShoppingCartOff className="text-xl" />
                      Esvaziar carrinho
                    </button>
                    <button
                      className="bg-red-600 min-w-36 px-2 py-4 text-white text-md rounded-xl"
                      onClick={handleFinishCart}
                    >
                      Finalizar
                    </button>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </section>
    </BaseLayout>
  );
};
